import { supabase } from '@/shared/lib/supabase';

/**
 * Cross-customer service-order worklist.
 *
 * Why this exists (AR-10, ARCHITECTURE_RULING_ADMIN_NAVIGATION_2026-08-30):
 * `/admin/services/:id` shipped with no `/admin/services`. The only list-shaped
 * page was `ServicesReportPage`, mounted at `/admin/reports/services` — so the
 * single path to a live R3,990 Studio order ran through a REPORT, and the
 * detail route's parent lived under a different path prefix entirely, which is
 * why nothing on that page could derive a breadcrumb.
 *
 * A report and a worklist answer different questions. The report keeps its
 * aggregates, SLA log and revenue-at-risk; this is the surface you WORK from.
 * They read the same table and must never disagree on a count.
 *
 * RLS: `service_order` is staff-readable; no view wrapper is needed. Emails and
 * assignee names are resolved in two batched lookups rather than per row.
 */

export type ServiceOrderListRow = {
  id: string;
  sku_code: string;
  status: string;
  customer_id: string;
  customer_email: string | null;
  customer_name: string | null;
  primary_fqdn: string | null;
  total_paid_cents: number;
  assignee_user_id: string | null;
  assignee_name: string | null;
  created_at: string;
  updated_at: string;
  brief_completed_at: string | null;
  delivered_at: string | null;
  complete_at: string | null;
};

/** Statuses that mean "no longer on anyone's desk". Used for the default
 *  filter, so the worklist opens on work rather than on history. */
export const CLOSED_SERVICE_ORDER_STATUSES = ['complete', 'cancelled'] as const;

type RawOrder = Omit<
  ServiceOrderListRow,
  'customer_email' | 'customer_name' | 'assignee_name'
>;

export const adminServiceOrdersService = {
  /** Every service order, newest first. Filtering is done client-side: the
   *  table is in the low hundreds of rows, and a round trip per filter change
   *  would cost more than it saves. Revisit if this passes ~2,000 rows. */
  async list(): Promise<ServiceOrderListRow[]> {
    const { data, error } = await supabase
      .from('service_order')
      .select(
        'id, sku_code, status, customer_id, primary_fqdn, total_paid_cents, assignee_user_id, created_at, updated_at, brief_completed_at, delivered_at, complete_at',
      )
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Could not load service orders: ${error.message}`);

    const rows = (data ?? []) as RawOrder[];
    if (rows.length === 0) return [];

    const customerIds = Array.from(new Set(rows.map((r) => r.customer_id)));
    const assigneeIds = Array.from(
      new Set(rows.map((r) => r.assignee_user_id).filter((v): v is string => Boolean(v))),
    );

    const [profiles, staff] = await Promise.all([
      customerIds.length
        ? supabase
            .from('customer_profile')
            .select('id, billing_email, full_name, company')
            .in('id', customerIds)
        : Promise.resolve({ data: [] as never[] }),
      assigneeIds.length
        ? supabase.from('staff_profile').select('id, display_name').in('id', assigneeIds)
        : Promise.resolve({ data: [] as never[] }),
    ]);

    const emailById = new Map<string, string | null>();
    const nameById = new Map<string, string | null>();
    (
      (profiles.data ?? []) as Array<{
        id: string;
        billing_email: string | null;
        full_name: string | null;
        company: string | null;
      }>
    ).forEach((p) => {
      emailById.set(p.id, p.billing_email);
      nameById.set(p.id, p.company ?? p.full_name);
    });

    const staffById = new Map<string, string | null>();
    ((staff.data ?? []) as Array<{ id: string; display_name: string | null }>).forEach((s) => {
      staffById.set(s.id, s.display_name);
    });

    return rows.map((r) => ({
      ...r,
      customer_email: emailById.get(r.customer_id) ?? null,
      customer_name: nameById.get(r.customer_id) ?? null,
      // A uuid fragment is not a name. When the lookup misses we say so rather
      // than rendering 8 hex characters at a human (the defect recorded in
      // STUDIO operational gap analysis, e5dc992).
      assignee_name: r.assignee_user_id
        ? (staffById.get(r.assignee_user_id) ?? 'Unknown staff member')
        : null,
    }));
  },
};
